// supervisor.ts
//
// `run` skeleton (spec §6.1, Req 2): acquire machine lock -> load
// effective-config -> doctor -> reconcile -> §18 beforeDispatch gate ->
// dispatch loop STUB. Full scheduler is P6 — this phase wires
// lock->config->doctor->reconcile->beforeDispatch only (Req 11: "keep
// supervisor.ts a thin skeleton, don't over-build").
//
// N39 drift rule: supervisor-config-drift-reconcile.ts (moved out, V2 phase 02).
//
// §18 beforeDispatch (code-reviewer-260909-p8-m4.md H1): the real dispatch
// LOOP (supervisor-mission-run.ts's runSupervisorUntilIdle, which re-checks
// this every tick) is P6/P9 scope, still not reachable from `autopilot run`
// (no production sessionDeps/buildPrompt wiring exists yet). Evaluating the
// gate ONCE here — with the REAL effective-config, real openAttemptIds, and
// a real `claude --version` probe — is what makes quiet hours/session cap/
// free disk/pin drift actually take effect on the `autopilot run` CLI path
// today, instead of only inside unit tests that call the gate directly.
import { join } from "node:path";
import { runAutopilotDoctorChecks } from "./autopilot-doctor.js";
// N9 (phase 05): shared probe from the neutral module — never from the
// CLI-wiring layer's own copy (that would invert the layering: the runtime
// supervisor layer must not depend on the CLI layer).
import { probeClaudeVersion as defaultProbeClaudeVersion } from "./claude-version-probe.js";
import { reconcileDelegatedAnswersFromJournal } from "./decision-auto-delegated-answer-reconcile.js";
import { type EffectiveConfigLoadResult, loadEffectiveConfig } from "./effective-config-loader.js";
import { acquireMachineLock, type HeldMachineLock } from "./mission-lock.js";
import { MissionStore } from "./mission-store.js";
import { reconcileConfigDrift } from "./supervisor-config-drift-reconcile.js";
import {
  type BeforeDispatchDecision,
  evaluateBeforeDispatch,
  readAttemptTimestampsIso,
  readOpenAttemptIds,
} from "./supervisor-dispatch-guards.js";

export interface SupervisorStartInput {
  missionId: string;
  workspaceRoot: string;
  missionWorkspace: string;
  sourceRepo: string;
  controlRoot: string;
  machineLockPath: string;
  globalConfigPath: string;
  leaseStaleSec?: number;
  now?: () => Date;
}

export interface SupervisorStartResult {
  lock: HeldMachineLock;
  effectiveConfig: EffectiveConfigLoadResult;
  doctorBlocksRun: boolean;
  /** Failed doctor checks as "name: detail" — WHY doctorBlocksRun (review P3 M2). */
  doctorFailures?: string[];
  driftActions: string[];
  /** §18 gate result — undefined only when effectiveConfig/doctor already blocked (never evaluated). */
  beforeDispatch?: BeforeDispatchDecision;
}

/**
 * `run` startup sequence: lock -> effective-config -> doctor -> reconcile.
 * Does NOT start the real dispatch loop (P6) — returns after the skeleton
 * steps so tests can assert ordering/behavior without a live scheduler.
 */
export async function startSupervisor(input: SupervisorStartInput): Promise<SupervisorStartResult> {
  const now = input.now ?? (() => new Date());

  // 1. Machine lock — one mission per machine (§9.4).
  const lock = await acquireMachineLock(input.machineLockPath, {
    missionId: input.missionId,
    leaseStaleSec: input.leaseStaleSec,
    now,
  });

  try {
    // 2. Effective-config — loaded ONCE, snapshot written to control root.
    const effectiveConfig = await loadEffectiveConfig({
      globalConfigPath: input.globalConfigPath,
      controlRoot: input.controlRoot,
    });
    if (!effectiveConfig.ok) {
      return { lock, effectiveConfig, doctorBlocksRun: true, driftActions: [] };
    }

    // 3. Doctor — opsChecks with REAL openAttemptIds (H2: never the fail-open
    // "undefined => []" default; pruneAttemptLogs fails closed on omission).
    const openAttemptIds = await readOpenAttemptIds(input.controlRoot);
    const doctorResult = await runAutopilotDoctorChecks({
      missionWorkspace: input.missionWorkspace,
      packRoot: join(input.missionWorkspace, ".claude", "pack"),
      controlRoot: input.controlRoot,
      opsChecks: {
        config: effectiveConfig.config,
        probeClaudeVersion: defaultProbeClaudeVersion,
        now,
        openAttemptIds,
      },
    });

    // 4. Reconcile — N39 config drift; journal-level reconcile is done by
    // MissionStore.open() itself (P3). The store stays open through step 5
    // so the cost/attempt ceiling guard can commit its WAITING_HUMAN receipt
    // (re-review 2026-09-26, A-F1: evaluated store-less before, so a breached
    // ceiling at startup was journaled as a retryable `blocked:<code>`).
    const store = await MissionStore.open(input.controlRoot);
    let driftActions: string[];
    let beforeDispatch: Awaited<ReturnType<typeof evaluateBeforeDispatch>>;
    try {
      driftActions = await reconcileConfigDrift({
        controlRoot: input.controlRoot,
        missionWorkspace: input.missionWorkspace,
        freshPermissionsAllowFallback: effectiveConfig.config.permissions_allow_fallback,
        freshConfigForKeyDiff: effectiveConfig.config as unknown as Record<string, unknown>,
        freshConfigV2ForKeyDiff: effectiveConfig.configV2,
      });
      // 4b. V2-1 review M1: an auto decision journaled right before a crash gets
      // its delegated answer back BEFORE any dispatch gate reads interview.json.
      await reconcileDelegatedAnswersFromJournal(input.controlRoot, now);

      // 5. §18 beforeDispatch gate — evaluated once at startup; the per-tick
      // re-check lives in supervisor-run-until-idle.ts.
      const attemptTimestampsIso = await readAttemptTimestampsIso(input.controlRoot);
      beforeDispatch = await evaluateBeforeDispatch({
        missionWorkspace: input.missionWorkspace,
        controlRoot: input.controlRoot,
        config: effectiveConfig.config,
        now: now(),
        attemptTimestampsIso,
        probeClaudeVersion: defaultProbeClaudeVersion,
        store,
      });
    } finally {
      await store.close();
    }

    return {
      lock,
      effectiveConfig,
      doctorBlocksRun: doctorResult.blocksRun,
      doctorFailures: doctorResult.checks
        .filter((c) => c.status === "fail")
        .map((c) => `${c.name}: ${c.detail}`),
      driftActions,
      beforeDispatch,
    };
  } catch (err) {
    await lock.release();
    throw err;
  }
}
